import React from 'react'
import { Box, Typography, Chip, TextField, InputAdornment } from '@mui/material'
import { Search } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { TopicType } from '@/utils/constants'
import AnimatedSquares from './AnimatedSquares'

// Helper to convert hex to rgba
const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const HeroContainer = styled(Box)<{ primaryColor: string; secondaryColor: string }>(
  ({ theme, primaryColor, secondaryColor }) => ({
    background: `
    linear-gradient(135deg, ${hexToRgba(primaryColor, 0.03)} 0%, ${hexToRgba(
      secondaryColor,
      0.02,
    )} 100%),
    radial-gradient(circle at top right, ${hexToRgba(primaryColor, 0.08)} 0%, transparent 50%),
    radial-gradient(circle at bottom left, ${hexToRgba(secondaryColor, 0.06)} 0%, transparent 50%),
    #ffffff
  `,
    backdropFilter: 'blur(20px)',
    borderRadius: '28px',
    padding: '56px 48px',
    marginBottom: '48px',
    marginTop: '20px',
    border: `2px solid ${hexToRgba(primaryColor, 0.08)}`,
    boxShadow: `
    0 20px 60px ${hexToRgba(primaryColor, 0.12)},
    0 6px 20px rgba(0, 0, 0, 0.04),
    0 0 0 1px ${hexToRgba(primaryColor, 0.05)},
    inset 0 2px 0 rgba(255, 255, 255, 0.8)
  `,
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',

    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `
      0 24px 72px ${hexToRgba(primaryColor, 0.16)},
      0 8px 24px rgba(0, 0, 0, 0.06),
      0 0 0 1px ${hexToRgba(primaryColor, 0.08)},
      inset 0 2px 0 rgba(255, 255, 255, 0.9)
    `,
    },

    [theme.breakpoints.down('md')]: {
      padding: '40px 28px',
      marginBottom: '28px',
    },

    // Floating decorative orb - top right
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-100px',
      right: '-100px',
      width: '300px',
      height: '300px',
      borderRadius: '50%',
      background: `radial-gradient(circle, ${hexToRgba(primaryColor, 0.1)} 0%, transparent 70%)`,
      animation: 'float 15s ease-in-out infinite',
      pointerEvents: 'none',
    },

    // Floating decorative orb - bottom left
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-120px',
      left: '-120px',
      width: '350px',
      height: '350px',
      borderRadius: '50%',
      background: `radial-gradient(circle, ${hexToRgba(secondaryColor, 0.12)} 0%, transparent 70%)`,
      animation: 'float 18s ease-in-out infinite',
      animationDelay: '2s',
      pointerEvents: 'none',
    },
  }),
)

const SearchBar = styled(TextField)<{ primaryColor: string }>(({ theme, primaryColor }) => ({
  '& .MuiOutlinedInput-root': {
    height: '52px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    fontSize: '15px',
    fontWeight: 500,
    border: '2px solid rgba(226, 232, 240, 0.8)',
    boxShadow: `
      0 6px 20px ${hexToRgba(primaryColor, 0.08)},
      0 2px 6px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 1)
    `,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

    '& fieldset': {
      border: '2px solid rgba(226, 232, 240, 0.8)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },

    '&:hover': {
      backgroundColor: '#ffffff',
      transform: 'translateY(-1px)',
      boxShadow: `
        0 8px 24px ${hexToRgba(primaryColor, 0.12)},
        inset 0 1px 0 rgba(255, 255, 255, 1)
      `,
      '& fieldset': {
        borderColor: hexToRgba(primaryColor, 0.3),
      },
    },

    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      transform: 'translateY(-1px)',
      boxShadow: `
        0 0 0 3px ${hexToRgba(primaryColor, 0.1)},
        0 8px 24px ${hexToRgba(primaryColor, 0.12)}
      `,
      '& fieldset': {
        borderColor: primaryColor,
      },
    },
  },
}))

interface HeroSectionProps {
  topicType: number
  pageTitle: string
  pageDescription: string
  searchQuery: string
  onSearchChange: (value: string) => void
  themeColors: {
    primary: string
    secondary: string
    light: string
    icon: React.ReactElement
    gradients: string[]
  }
  children?: React.ReactNode
}

const HeroSection: React.FC<HeroSectionProps> = ({
  topicType,
  pageTitle,
  pageDescription,
  searchQuery,
  onSearchChange,
  themeColors,
  children,
}) => {
  const getBadgeLabel = () => {
    if (topicType === TopicType.PATTERN) return 'Patterns & Designs'
    if (topicType === TopicType.ALBUM) return 'Albums & Collections'
    if (topicType === TopicType.GALLERY) return 'Galleries & Inspiration'
    return 'Discover & Create'
  }

  const getSearchPlaceholder = () => {
    if (topicType === TopicType.PATTERN) return 'Search for patterns...'
    if (topicType === TopicType.ALBUM) return 'Search for albums...'
    if (topicType === TopicType.GALLERY) return 'Search for galleries...'
    return 'Search for creations...'
  }

  return (
    <HeroContainer primaryColor={themeColors.primary} secondaryColor={themeColors.secondary}>
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Left Content */}
        <Box sx={{ maxWidth: '720px' }}>
          {/* Badge */}
          <Chip
            icon={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& svg': {
                    width: '16px',
                    height: '16px',
                    fill: '#ffffff',
                    color: '#ffffff',
                    '& path': {
                      fill: '#ffffff',
                    },
                  },
                }}
              >
                {themeColors.icon}
              </Box>
            }
            label={getBadgeLabel()}
            sx={{
              height: '32px',
              background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.secondary} 100%)`,
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 700,
              border: 'none',
              boxShadow: `0 4px 12px ${themeColors.light}`,
              mb: 2.5,
              '& .MuiChip-icon': {
                color: '#ffffff',
                marginLeft: '8px',
              },
            }}
          />

          {/* Main Title */}
          <Typography
            sx={{
              fontSize: { xs: '32px', sm: '42px', md: '48px' },
              fontWeight: 800,
              color: '#0f172a',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              mb: 2,
              background:
                topicType === TopicType.ALBUM
                  ? `linear-gradient(135deg, #0f172a 0%, ${themeColors.primary} 60%, ${themeColors.secondary} 100%)`
                  : `linear-gradient(135deg, #0f172a 0%, ${themeColors.primary} 60%, ${themeColors.secondary} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {pageTitle}
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 500,
              color: '#64748b',
              lineHeight: 1.6,
              mb: 4,
            }}
          >
            {pageDescription}
          </Typography>

          {/* Search Bar */}
          <Box sx={{ mb: topicType === TopicType.ALL ? 4 : 0 }}>
            <SearchBar
              primaryColor={themeColors.primary}
              fullWidth
              placeholder={getSearchPlaceholder()}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search sx={{ color: themeColors.primary, fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Category Filters (passed as children) */}
          {children}
        </Box>

        {/* Right Decorative Pattern Animation */}
        <AnimatedSquares themeColors={themeColors} />
      </Box>
    </HeroContainer>
  )
}

export default HeroSection
